import { DeleteOperation } from '@infuse/api/common';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericDeleteOrganisation = ({
  apiFn,
  forcedParentId
}: {
  apiFn: DeleteOperation<never>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, { parentId?: string; id: string }>({
    mutationFn: async ({ parentId, id }: { parentId?: string; id: string }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        await apiFn(parentId ? { parentId: parentId, id: id } : { id: id });
        notify({
          title: t('iam.organisations.header.delete_organisation'),
          message: t('iam.organisations.notify.delete_organisation_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.organisations.header.delete_organisation'),
          message: getAxiosErrorMessage(e, t('iam.organisations.notify.delete_organisation_failed'))
        });
        throw e;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organisations'] });
    }
  });

  return {
    ...mutation
  };
};
